import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import { Link } from "react-router-dom";
import { AccountCircle, AdminPanelSettings, Logout as LogoutIcon, Assignment as OrdersIcon, SupportAgent as SupportIcon } from "@mui/icons-material";
import NotificationBell from "./components/NotificationBell";

const StyledRoot = styled('div')(({ theme }) => ({
  flexGrow: 1,
}));

const StyledMenuButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  flexGrow: 1,
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: "#1a237e", // Dark blue text
  textDecoration: "none",
  marginRight: theme.spacing(2),
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  minHeight: 72, // Match AppBar height
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const StyledNavLinks = styled('div')(({ theme }) => ({
  display: "flex",
}));

const StyledLogoutButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  color: "#1a237e",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    minWidth: "auto",
    padding: theme.spacing(0.5, 1),
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#e3f2fd", // Light blue background
  zIndex: 1301,
  minHeight: 72, // Slightly reduced height
  boxShadow: "none", // Remove drop shadow
}));

const StyledContent = styled('div')(({ theme }) => ({
  paddingTop: 72, // Updated AppBar height
  [theme.breakpoints.up("sm")]: {
    paddingTop: 72, // Match new header height
  },
}));

interface HeaderProps {
  onLogout: () => void;
  isPharmacy: boolean;
  isLoggedIn: boolean;
  onAdminToggle?: () => void;
  showAdmin?: boolean;
  onOrdersClick?: () => void;
  mustChangePassword?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onLogout, isPharmacy, isLoggedIn, onAdminToggle, showAdmin, onOrdersClick, mustChangePassword }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <StyledRoot>
      <div>
        <StyledAppBar position="fixed">
          <StyledToolbar>
            <StyledLink to={mustChangePassword ? "/login" : (isPharmacy ? "/pharmacy" : "/")} onClick={mustChangePassword ? onLogout : (isPharmacy ? onOrdersClick : undefined)}>
              <StyledMenuButton
                edge="start"
                sx={{ color: "#1a237e" }}
                aria-label="menu"
              >
                <img
                  src="/logo_new.png"
                  alt="The Chemist Shop"
                  style={{ height: isMobile ? 45 : 63 }}
                />
              </StyledMenuButton>
            </StyledLink>

            {/* Desktop text nav links */}
            {!mustChangePassword && (
            <StyledNavLinks sx={{ display: { xs: "none", sm: "flex" } }}>
              <Typography variant="h6">
                {isPharmacy ? (
                  <StyledLink to="/pharmacy" onClick={onOrdersClick}>
                    Orders
                  </StyledLink>
                ) : (
                  <StyledLink to="/">
                    Products
                  </StyledLink>
                )}
              </Typography>
            </StyledNavLinks>
            )}

            <div style={{ display: "flex", alignItems: "center" }}>
              {isLoggedIn ? (
                <>
                  {/* Desktop History text link */}
                  {!isPharmacy && !mustChangePassword && (
                    <Box sx={{ display: { xs: "none", sm: "inline" } }}>
                      <StyledLink to="/history" style={{ marginRight: 16 }}>
                        History
                      </StyledLink>
                    </Box>
                  )}
                  {/* Mobile Orders icon */}
                  {!isPharmacy && !mustChangePassword && (
                    <Link to="/history" style={{ color: "#1a237e" }}>
                      <IconButton sx={{ color: "#1a237e", display: { xs: "inline-flex", sm: "none" } }} aria-label="orders">
                        <OrdersIcon style={{ width: 32, height: 32 }} />
                      </IconButton>
                    </Link>
                  )}
                  {!isPharmacy && !mustChangePassword && (
                    <NotificationBell isPharmacy={false} tokenKey="providerToken" />
                  )}
                  {!isPharmacy && !mustChangePassword && (
                    <Link to="/support" style={{ color: "#1a237e" }}>
                      <IconButton sx={{ color: "#1a237e" }} aria-label="support">
                        <SupportIcon style={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32 }} />
                      </IconButton>
                    </Link>
                  )}
                  {!isPharmacy && !mustChangePassword && (
                    <Link
                      to="/profile"
                      style={{ textDecoration: "none", color: "#1a237e" }}
                    >
                      <IconButton
                        edge="end"
                        sx={{ color: "#1a237e" }}
                        aria-label="user-profile"
                      >
                        <AccountCircle style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }} />
                      </IconButton>
                    </Link>
                  )}
                  {isPharmacy && !mustChangePassword && (
                    <Link to="/pharmacy-profile" style={{ color: "#1a237e" }}>
                      <IconButton
                        sx={{ color: "#1a237e" }}
                        aria-label="pharmacy-profile"
                      >
                        <AccountCircle style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }} />
                      </IconButton>
                    </Link>
                  )}
                  {isPharmacy && !mustChangePassword && (
                    <NotificationBell isPharmacy={true} tokenKey="pharmacistToken" />
                  )}
                  {isPharmacy && !mustChangePassword && (
                    <Link to="/pharmacy?admin=true" style={{ color: "#1a237e" }}>
                      <IconButton
                        sx={{ color: "#1a237e" }}
                        aria-label="admin-panel"
                        style={{
                          marginRight: 8,
                          backgroundColor: showAdmin ? 'rgba(26,35,126,0.1)' : 'transparent'
                        }}
                      >
                        <AdminPanelSettings style={{ width: 32, height: 32 }} />
                      </IconButton>
                    </Link>
                  )}
                  <StyledLogoutButton onClick={onLogout}>
                    Logout
                  </StyledLogoutButton>
                </>
              ) : (
                <Link to="/login" style={{ textDecoration: "none" }}>
                  <StyledLogoutButton>
                    Login
                  </StyledLogoutButton>
                </Link>
              )}
            </div>
          </StyledToolbar>
        </StyledAppBar>
        <StyledContent />
      </div>
      <br />
    </StyledRoot>
  );
};

export default Header;
